import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";

// Import API helper
import { getUser } from "../utils/api";

// Import your step components
import StepEmailVerification from "../components/StepEmailVerification";
import StepPersonalInfo from "../components/StepPersonalInfo";
import StepAddress from "../components/StepAddress";
import StepDocuments from "../components/StepDocuments";
import StepReview from "../components/StepReview";

const steps = [
  "Email Verification",
  "Personal Information",
  "Address Details",
  "Documents",
  "Review & Submit",
];

const KYCForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [completedStep, setCompletedStep] = useState(
    Number(localStorage.getItem("kycCompletedStep")) || 0
  );
  const [formData, setFormData] = useState({});
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🔹 Load user + restore progress & form data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await getUser();
        setUser(res);

        // Restore saved form data if available
        const savedData = localStorage.getItem("kycFormData");
        if (savedData) {
          setFormData(JSON.parse(savedData));
        } else {
          // Initialize with user email at least
          setFormData({ email: res.email });
        }

        // Restore saved step
        const savedStep = Number(localStorage.getItem("kycCompletedStep")) || 0;
        setActiveStep(savedStep);
      } catch (err) {
        console.error("Failed to fetch user", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  // 🔹 Persist formData to localStorage
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      localStorage.setItem("kycFormData", JSON.stringify(formData));
    }
  }, [formData]);

  const handleNext = (data = {}) => {
    setFormData((prev) => {
      const updated = { ...prev, ...data };
      localStorage.setItem("kycFormData", JSON.stringify(updated)); // persist immediately
      return updated;
    });

    setActiveStep((prev) => {
      const nextStep = Math.min(prev + 1, steps.length - 1);

      setCompletedStep((old) => {
        const maxStep = Math.max(old, nextStep);
        localStorage.setItem("kycCompletedStep", maxStep);
        return maxStep;
      });

      return nextStep;
    });
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const renderStep = () => {
    if (!user) return <p className="text-slate-400">Loading user info...</p>;

    switch (activeStep) {
      case 0:
        return (  
          <StepEmailVerification
  user={user}
  onVerified={(email) => {
    handleNext({ email });
  }}
/>

        );
      case 1:
        return (
          <StepPersonalInfo
            data={formData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <StepAddress data={formData} onNext={handleNext} onBack={handleBack} />
        );
      case 3:
        return (
          <StepDocuments
            data={formData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <StepReview data={formData} onNext={handleNext} onBack={handleBack} />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[70vh]">
        <p className="text-slate-400">Fetching user...</p>
      </div>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
        color: "white",
        p: 3,
      }}
    >
      {/* Header */}
      <Paper
        elevation={6}
        sx={{
          p: 2,
          mb: 4,
          borderRadius: 3,
          backdropFilter: "blur(10px)",
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.2)",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            background: "linear-gradient(90deg,#00f0ff,#ff00f0)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          KYC Verification Portal
        </Typography>
      </Paper>

      {/* Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  color:
                    index === activeStep
                      ? "#00f0ff !important"
                      : index < activeStep
                      ? "#ff00f0 !important"
                      : "rgba(255,255,255,0.3)",
                  filter:
                    index === activeStep
                      ? "drop-shadow(0 0 6px #00f0ff)"
                      : index < activeStep
                      ? "drop-shadow(0 0 6px #ff00f0)"
                      : "none",
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step container */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ duration: 0.5 }}
            style={{
              width: "100%",
              maxWidth: 600,
              padding: "2rem",
              borderRadius: "1rem",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              backdropFilter: "blur(12px)",
              boxShadow: "0 0 20px rgba(0,0,0,0.5)",
            }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default KYCForm;